/**
 * Contact Item Component
 * 
 * Reusable component that displays a single contact method with:
 * - An icon (email, phone, location, etc.)
 * - A title/label
 * - Content (the actual contact information)
 * - Optional link (makes content clickable)
 * 
 * The component intelligently handles different icon types and link types,
 * making it flexible for various contact methods.
 * 
 * @component
 * @param {Object} props - Component properties
 * @param {JSX.Element|string} props.icon - Icon to display (SVG component or string)
 * @param {string} props.title - Title/label for the contact method
 * @param {string} props.content - The actual contact information to display
 * @param {string|null} props.link - Optional link URL (mailto:, tel:, https://, or null)
 * @returns {JSX.Element} A single contact item with icon, title, and content
 */

import React from 'react';
import './contact-item-style.css';

const ContactItem = ({ icon, title, content, link }) => {
  /**
   * Renders the content as either a link or plain text
   * If a link is provided, renders as a clickable anchor tag
   * Otherwise, renders as a paragraph
   * @returns {JSX.Element} Content as link or paragraph
   */
  const renderContent = () => {
    if (link) {
      return (
        <a href={link} className="contact-item__link" target="_blank" rel="noopener noreferrer">
          {content}
        </a>
      );
    }
    return <p className="contact-item__content">{content}</p>;
  };

  /**
   * Renders the icon appropriately based on its type
   * If icon is a string, wraps it in a span
   * Otherwise, renders the icon component directly (typically an SVG)
   * @returns {JSX.Element} Rendered icon
   */
  const renderIcon = () => {
    if (typeof icon === 'string') {
      return <span>{icon}</span>;
    }
    return icon;
  };

  return (
    <div className="contact-item">
      <div className="contact-item__icon">
        {renderIcon()}
      </div>
      <div className="contact-item__info">
        <h3 className="contact-item__title">{title}</h3>
        {renderContent()}
      </div>
    </div>
  );
};

export default ContactItem;
